import {
  Archive,
  Check,
  Clapperboard,
  Clipboard,
  Code2,
  Download,
  Globe2,
  Image,
  Save,
  Send,
  ShieldAlert,
  Users,
} from "lucide-react";
import { useEffect, useState, type FormEvent } from "react";
import { apiFetch, ApiError } from "../../lib/api";
import { chatPrivacyPassus } from "../../lib/chat-privacy";
import {
  buildAutoIframeSnippet,
  buildIframeSnippet,
  buildWidgetSnippet,
} from "../../lib/embed";
import { useAdmin } from "./AdminShell";

type Tab = "branding" | "team" | "integrations" | "operations";

type EmbedVariant = "widget" | "iframe-auto" | "iframe";
type EmbedSource = "event" | "current" | "archive";

interface Member {
  id: string;
  role: string;
  user: { id: string; name: string; email: string; image: string | null };
}

export function AdminSettingsPage() {
  const { data, refresh } = useAdmin();
  const [tab, setTab] = useState<Tab>("branding");
  const [members, setMembers] = useState<Member[]>([]);
  const [saved, setSaved] = useState("");
  const [copied, setCopied] = useState("");

  const loadSecondary = () => {
    apiFetch<Member[]>("/api/admin/members").then(setMembers);
  };
  useEffect(loadSecondary, []);

  const flash = (message: string) => {
    setSaved(message);
    window.setTimeout(() => setSaved(""), 2_200);
  };

  const saveBranding = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    try {
      await apiFetch("/api/admin/tenant/settings", {
        method: "PATCH",
        body: JSON.stringify({
          primaryColor: form.get("primaryColor"),
          secondaryColor: form.get("secondaryColor"),
          logoUrl: form.get("logoUrl") || null,
          locale: form.get("locale"),
          timezone: form.get("timezone"),
          submissionsEnabled: form.get("submissionsEnabled") === "on",
          chatEnabled: form.get("chatEnabled") === "on",
          publicStatsEnabled: form.get("publicStatsEnabled") === "on",
          isPaused: form.get("isPaused") === "on",
          watermarkEnabled: form.get("watermarkEnabled") === "on",
          watermarkUrl: form.get("watermarkUrl") || null,
          watermarkPosition: form.get("watermarkPosition"),
          watermarkOpacity: Number(form.get("watermarkOpacity")),
          imprintUrl: form.get("imprintUrl") || null,
          privacyUrl: form.get("privacyUrl") || null,
          aiAltTextEnabled: form.get("aiAltTextEnabled") === "on",
          chatTurnstileSiteKey:
            (form.get("chatTurnstileSiteKey") as string) || null,
          videoEnabled: form.get("videoEnabled") === "on",
        }),
      });
      await refresh();
      flash("Branding gespeichert");
    } catch (error) {
      if (
        error instanceof ApiError &&
        (error.code === "VIDEO_NOT_CONFIGURED" ||
          error.code === "VIDEO_PROVIDER_ERROR")
      ) {
        flash(
          "Video konnte nicht aktiviert werden: Bunny Stream ist auf dem Server nicht konfiguriert oder nicht erreichbar.",
        );
        await refresh();
        return;
      }
      flash("Speichern fehlgeschlagen");
    }
  };

  const invite = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const formEl = event.currentTarget;
    const form = new FormData(formEl);
    try {
      await apiFetch("/api/admin/members/invite", {
        method: "POST",
        body: JSON.stringify({
          email: form.get("email"),
          role: form.get("role"),
        }),
      });
      formEl.reset();
      flash("Einladung versendet");
    } catch (error) {
      flash(
        error instanceof ApiError
          ? `Fehler: ${error.message}`
          : "Einladung fehlgeschlagen",
      );
    }
  };

  const copy = async (label: string, value: string) => {
    await navigator.clipboard.writeText(value);
    setCopied(label);
    window.setTimeout(() => setCopied(""), 1_500);
  };

  const exportArchive = async () => {
    const archive = await apiFetch<Record<string, unknown>>(
      "/api/admin/archive/export",
    );
    const blob = new Blob([JSON.stringify(archive, null, 2)], {
      type: "application/json",
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `${data.tenant.slug}-vzlive-archive.json`;
    link.click();
    URL.revokeObjectURL(url);
  };

  const currentEvent =
    data.events.find((event) => event.status === "live") ?? data.events[0];

  const [embedVariant, setEmbedVariant] = useState<EmbedVariant>("widget");
  const [embedSource, setEmbedSource] = useState<EmbedSource>("current");
  const [embedTheme, setEmbedTheme] = useState<"auto" | "light" | "dark">(
    "auto",
  );
  const [embedBrandHidden, setEmbedBrandHidden] = useState(false);

  const embedOptions = {
    origin: window.location.origin,
    event: embedSource === "event" ? currentEvent : null,
    theme: embedTheme,
    showBrand: !embedBrandHidden,
    source: embedSource === "event" ? undefined : embedSource,
  } as const;
  const embedPreview =
    embedSource === "event" && !currentEvent
      ? "Lege zuerst ein Event an."
      : embedVariant === "widget"
        ? buildWidgetSnippet(embedOptions)
        : embedVariant === "iframe-auto"
          ? buildAutoIframeSnippet(embedOptions)
          : buildIframeSnippet(embedOptions);

  return (
    <div className="admin-page settings-page">
      <header className="admin-page-header">
        <div>
          <h1>Einstellungen</h1>
          <p>
            Marke, Team, Integrationen und Betrieb. Events findest du im eigenen
            Bereich.
          </p>
        </div>
        {saved && (
          <span className="save-toast">
            <Check /> {saved}
          </span>
        )}
      </header>

      <nav className="settings-tabs" aria-label="Einstellungsbereiche">
        {(
          [
            ["branding", Image, "Marke"],
            ["team", Users, "Team"],
            ["integrations", Code2, "Integrationen"],
            ["operations", Archive, "Betrieb"],
          ] as const
        ).map(([value, Icon, label]) => (
          <button
            key={value}
            className={tab === value ? "active" : ""}
            onClick={() => setTab(value)}
          >
            <Icon size={17} /> {label}
          </button>
        ))}
      </nav>

      {tab === "branding" && (
        <form onSubmit={saveBranding} className="settings-grid">
          <section className="settings-panel">
            <header>
              <h2>Marke</h2>
              <Globe2 />
            </header>
            <div className="settings-form">
              <label>
                <span>Primärfarbe</span>
                <div className="color-input">
                  <input
                    name="primaryColor"
                    type="color"
                    defaultValue={data.tenant.primaryColor}
                  />
                  <input
                    defaultValue={data.tenant.primaryColor}
                    onChange={(event) => {
                      const color = event.currentTarget
                        .previousElementSibling as HTMLInputElement;
                      color.value = event.target.value;
                    }}
                  />
                </div>
              </label>
              <label>
                <span>Sekundärfarbe</span>
                <div className="color-input">
                  <input
                    name="secondaryColor"
                    type="color"
                    defaultValue={data.tenant.secondaryColor}
                  />
                  <input defaultValue={data.tenant.secondaryColor} />
                </div>
              </label>
              <label className="wide">
                <span>Logo-URL</span>
                <input
                  name="logoUrl"
                  type="url"
                  defaultValue={data.tenant.logoUrl ?? ""}
                />
              </label>
              <label>
                <span>Sprache</span>
                <select name="locale" defaultValue={data.tenant.locale}>
                  <option value="de">Deutsch</option>
                  <option value="en">English</option>
                </select>
              </label>
              <label>
                <span>Zeitzone</span>
                <input name="timezone" defaultValue={data.tenant.timezone} />
              </label>
            </div>
          </section>
          <section className="settings-panel">
            <header>
              <h2>Medien-Verarbeitung</h2>
              <Image />
            </header>
            <div className="settings-form">
              <label className="switch-row wide">
                <span>Wasserzeichen aktiv</span>
                <input
                  name="watermarkEnabled"
                  type="checkbox"
                  role="switch"
                  defaultChecked={data.tenant.watermarkEnabled}
                />
              </label>
              <label className="wide">
                <span>Wasserzeichen-URL</span>
                <input
                  name="watermarkUrl"
                  type="url"
                  defaultValue={data.tenant.watermarkUrl ?? ""}
                />
              </label>
              <label>
                <span>Position</span>
                <select
                  name="watermarkPosition"
                  defaultValue={data.tenant.watermarkPosition}
                >
                  <option value="northwest">oben links</option>
                  <option value="northeast">oben rechts</option>
                  <option value="center">mittig</option>
                  <option value="southwest">unten links</option>
                  <option value="southeast">unten rechts</option>
                </select>
              </label>
              <label>
                <span>Deckkraft</span>
                <input
                  name="watermarkOpacity"
                  type="number"
                  min=".1"
                  max="1"
                  step="0.01"
                  defaultValue={data.tenant.watermarkOpacity}
                />
              </label>
              <label className="switch-row wide">
                <span>KI-Alt-Text-Vorschläge</span>
                <input
                  name="aiAltTextEnabled"
                  type="checkbox"
                  role="switch"
                  defaultChecked={data.tenant.aiAltTextEnabled}
                />
              </label>
            </div>
          </section>
          <section className="settings-panel">
            <header>
              <h2>Video (Bunny Stream)</h2>
              <Clapperboard />
            </header>
            <div className="settings-form">
              <label className="switch-row wide">
                <span>Video-Beiträge aktivieren</span>
                <input
                  // Key hält die Checkbox synchron mit dem Server-Stand:
                  // nach fehlgeschlagener Aktivierung springt sie zurück.
                  key={data.tenant.videoEnabled ? "video-on" : "video-off"}
                  name="videoEnabled"
                  type="checkbox"
                  role="switch"
                  defaultChecked={data.tenant.videoEnabled}
                />
              </label>
              <p className="settings-video-status">
                {data.tenant.bunnyCollectionId
                  ? "Video-Collection für diesen Tenant ist eingerichtet. Uploads laufen resumable direkt zu Bunny Stream; Encoding und Auslieferung übernimmt Bunny."
                  : "Beim ersten Aktivieren wird automatisch eine eigene Video-Collection für diesen Tenant angelegt."}
              </p>
              <p className="settings-video-status settings-video-status--soon">
                <strong>Demnächst:</strong> Livestreams. Bunny Stream Live ist
                derzeit in geschlossener Vorschau – wir aktivieren es, sobald es
                allgemein verfügbar ist.
              </p>
            </div>
          </section>
          <section className="settings-panel">
            <header>
              <h2>Reader</h2>
              <ShieldAlert />
            </header>
            <div className="settings-form">
              <label className="switch-row wide">
                <span>Leser-Einreichungen</span>
                <input
                  name="submissionsEnabled"
                  type="checkbox"
                  role="switch"
                  defaultChecked={data.tenant.submissionsEnabled}
                />
              </label>
              <label className="switch-row wide">
                <span>Live-Stats öffentlich</span>
                <input
                  name="publicStatsEnabled"
                  type="checkbox"
                  role="switch"
                  defaultChecked={data.tenant.publicStatsEnabled}
                />
              </label>
              <label className="switch-row wide">
                <span>Live-Chat für den Tenant freigeben</span>
                <input
                  name="chatEnabled"
                  type="checkbox"
                  role="switch"
                  defaultChecked={data.tenant.chatEnabled}
                />
              </label>
              <label className="wide">
                <span>Chat-Turnstile-Sitekey</span>
                <input
                  name="chatTurnstileSiteKey"
                  maxLength={200}
                  placeholder="leer = globaler Sitekey"
                  defaultValue={data.tenant.chatTurnstileSiteKey ?? ""}
                />
              </label>
              <label className="wide">
                <span>Impressum-URL</span>
                <input
                  name="imprintUrl"
                  type="url"
                  defaultValue={data.tenant.imprintUrl ?? ""}
                />
              </label>
              <label className="wide">
                <span>Datenschutz-URL</span>
                <input
                  name="privacyUrl"
                  type="url"
                  defaultValue={data.tenant.privacyUrl ?? ""}
                />
              </label>
            </div>
          </section>
          <section className="settings-panel">
            <header>
              <h2>VZ Live · Datenschutzerklärung</h2>
              <Code2 />
            </header>
            <p>
              Passus für eure Datenschutzerklärung zur Nutzung von VZ Live.
              Falls ihr Turnstile nicht nutzt, lasst den letzten Absatz weg.{" "}
              {data.tenant.chatTurnstileSiteKey ? "Turnstile aktiv." : ""}
            </p>
            <textarea
              readOnly
              rows={12}
              className="settings-privacy-copy"
              value={chatPrivacyPassus(data.tenant.locale)}
              onFocus={(focus) => focus.currentTarget.select()}
            />
            <button
              type="button"
              onClick={() =>
                void copy("chat-privacy", chatPrivacyPassus(data.tenant.locale))
              }
            >
              {copied === "chat-privacy" ? <Check /> : <Clipboard />}{" "}
              {copied === "chat-privacy" ? "Kopiert" : "Passus kopieren"}
            </button>
          </section>
          <section className="settings-panel settings-panel--danger">
            <header>
              <h2>Tenant-Pause</h2>
              <ShieldAlert />
            </header>
            <p>
              Stoppt die Live-Darstellung mandantenweit. Archiv und bisherige
              Beiträge bleiben lesbar.
            </p>
            <label className="switch-row">
              <span>Gesamten Tenant pausieren</span>
              <input
                name="isPaused"
                type="checkbox"
                role="switch"
                defaultChecked={data.tenant.isPaused}
              />
            </label>
          </section>
          <footer className="settings-save">
            <button type="submit">
              <Save /> Änderungen speichern
            </button>
          </footer>
        </form>
      )}

      {tab === "team" && (
        <div className="settings-grid">
          <section className="settings-panel settings-panel--wide">
            <header>
              <h2>Redaktionsteam</h2>
              <Users />
            </header>
            <div className="member-list">
              {members.map((entry) => (
                <article key={entry.id}>
                  <span>{entry.user.name.slice(0, 1).toUpperCase()}</span>
                  <div>
                    <strong>{entry.user.name}</strong>
                    <small>{entry.user.email}</small>
                  </div>
                  <b>{entry.role}</b>
                </article>
              ))}
            </div>
          </section>
          <section className="settings-panel">
            <header>
              <h2>Person hinzufügen</h2>
              <Send />
            </header>
            <form onSubmit={invite} className="settings-form">
              <label className="wide">
                <span>E-Mail</span>
                <input name="email" type="email" required />
              </label>
              <label className="wide">
                <span>Rolle</span>
                <select name="role">
                  <option value="reporter">Reporter (nur Feldberichte)</option>
                  <option value="editor">Editor</option>
                  <option value="admin">Admin</option>
                </select>
              </label>
              <button type="submit">
                <Send /> Einladung senden
              </button>
            </form>
          </section>
        </div>
      )}

      {tab === "integrations" && (
        <div className="settings-grid">
          <section className="settings-panel settings-panel--code">
            <header>
              <h2>Website-Einbindung</h2>
              <Code2 />
            </header>
            <p>
              Drei Varianten für die eigene Seite – Astro, WordPress oder
              beliebiges CMS. „Natives Widget" erbt Schrift und Farbschema der
              Wirtsseite und wächst mit dem Feed.
            </p>
            <div className="embed-builder">
              <div
                className="embed-builder__choices"
                role="group"
                aria-label="Einbindungsvariante"
              >
                {(
                  [
                    ["widget", "Natives Widget (empfohlen)"],
                    ["iframe-auto", "iframe · wächst mit"],
                    ["iframe", "iframe · feste Höhe"],
                  ] as const
                ).map(([value, label]) => (
                  <label key={value} className="switch-row">
                    <input
                      type="radio"
                      name="embedVariant"
                      value={value}
                      checked={embedVariant === value}
                      onChange={() => setEmbedVariant(value)}
                    />
                    <span>{label}</span>
                  </label>
                ))}
              </div>
              <label className="wide">
                <span>Quelle</span>
                <select
                  value={embedSource}
                  onChange={(change) =>
                    setEmbedSource(change.target.value as EmbedSource)
                  }
                >
                  <option value="current">
                    Immer das aktuelle Event (/embed/current)
                  </option>
                  <option value="archive">
                    Archiv ohne Feed (/embed/archive)
                  </option>
                  <option value="event">
                    {currentEvent?.title ?? "Einzelnes Event"}
                  </option>
                </select>
              </label>
              {embedVariant === "widget" && (
                <>
                  <label className="wide">
                    <span>Farbschema</span>
                    <select
                      value={embedTheme}
                      onChange={(change) =>
                        setEmbedTheme(change.target.value as typeof embedTheme)
                      }
                    >
                      <option value="auto">
                        Automatisch (Folgt der Wirtsseite)
                      </option>
                      <option value="light">Hell</option>
                      <option value="dark">Dunkel</option>
                    </select>
                  </label>
                  <label className="switch-row wide">
                    <span>Tenant-Logo im Widget</span>
                    <input
                      type="checkbox"
                      role="switch"
                      checked={!embedBrandHidden}
                      onChange={(change) =>
                        setEmbedBrandHidden(!change.target.checked)
                      }
                    />
                  </label>
                </>
              )}
              <pre>
                <code>{embedPreview}</code>
              </pre>
              <button
                type="button"
                disabled={embedSource === "event" && !currentEvent}
                onClick={() => void copy("embed", embedPreview)}
              >
                {copied === "embed" ? <Check /> : <Clipboard />}{" "}
                {copied === "embed" ? "Kopiert" : "Code kopieren"}
              </button>
            </div>
          </section>
        </div>
      )}

      {tab === "operations" && (
        <div className="settings-grid">
          <section className="settings-panel">
            <header>
              <h2>Archiv-Export</h2>
              <Archive />
            </header>
            <p>
              Exportiert Events, Posts, Mediendaten, Freigaben,
              Plugin-Konfiguration und aggregierte Statistik als versioniertes
              JSON.
            </p>
            <button onClick={() => void exportArchive()}>
              <Download /> Archiv exportieren
            </button>
          </section>
          <section className="settings-panel">
            <header>
              <h2>{data.tenant.host}</h2>
              <Globe2 />
            </header>
            <p>
              Reader und Live-Desk teilen sich diese Tenant-Domain. TLS und
              Host-Zuordnung werden durch die Deployment-Plattform verwaltet.
            </p>
            <button onClick={() => void copy("domain", data.tenant.host)}>
              {copied === "domain" ? <Check /> : <Clipboard />} Domain kopieren
            </button>
          </section>
        </div>
      )}
    </div>
  );
}
